import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Typography } from '@mui/material';
import { toast } from 'react-toastify';

import {
  ConfirmActionModal,
  PageContentLayout,
} from '@tassomutuo-backoffice/ui';
import {
  getAllTeamGroup,
  getTeamGroupMember,
  removeMemberFromGroup,
} from '../../../services/api/team/team';
import TeamGroupMembersTable from './components/TeamGroupMembersTable';
import { TeamMember } from '@tassomutuo-backoffice/types';
import { selectUser } from '../../../store/user/userSelectors';
import { useUserStore } from '../../../store/user';

const defaultPageModel = {
  page: 0,
  pageSize: 10,
};

const TeamGroupDetailPage = () => {
  const user = useUserStore(selectUser);

  const { groupId, teamId } = useParams();
  const queryClient = useQueryClient();

  const [memberToDelete, setMemberToDelete] = useState<TeamMember | null>(null);

  const [membersTableState, setMembersTableState] = useState({
    pagination: defaultPageModel,
    filterEmail: '',
  });

  const { data: membersData, isPending: isPendingTeamMembers } = useQuery({
    queryKey: ['TeamGroupMembers', groupId, membersTableState.pagination],
    queryFn: () =>
      getTeamGroupMember(groupId!, {
        page: membersTableState.pagination.page + 1,
        pageSize: membersTableState.pagination.pageSize,
      }),
    enabled: !!groupId,
  });

  const {
    isPending: isPendingTeamGroups,
    data: teamGroups,
    // error,
  } = useQuery({
    queryKey: ['TeamGroups', teamId],
    queryFn: () => getAllTeamGroup(teamId || ''),
    enabled: !!teamId,
  });

  const removeMemberFromGroupMutation = useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      removeMemberFromGroup(groupId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['TeamMembers', teamId],
      });
      queryClient.invalidateQueries({
        queryKey: ['TeamGroups', teamId],
      });
      queryClient.invalidateQueries({
        queryKey: ['TeamGroupMembers', groupId, membersTableState.pagination],
      });
      toast.success('Membro rimosso dal gruppo!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const onConfirmRemoveMemberFromGroup = async () => {
    await removeMemberFromGroupMutation.mutateAsync({
      groupId: groupId!,
      userId: memberToDelete!.id,
    });
    setMemberToDelete(null);
  };

  const teamGroup = teamGroups?.find((group) => group.id === groupId);
  const isAdmin = user?.role === 'admin';

  return (
    <>
      <Box px={4} pb={2} mt={6}>
        <Link
          to={`/admin/teams/${teamId}`}
          style={{
            textDecoration: 'none',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ArrowBackIcon />
            <Typography fontWeight="bold" color="primary">
              Indietro
            </Typography>
          </Box>
        </Link>
      </Box>
      <PageContentLayout
        title={`Gruppo ${teamGroup?.name || ''}`}
        error={null}
        isLoading={isPendingTeamGroups}
      >
        <Box>
          <TeamGroupMembersTable
            rowCount={membersData?.totalResults || 0}
            isPending={isPendingTeamMembers}
            paginationModel={membersTableState.pagination}
            setPaginationModel={(paginationModel) =>
              setMembersTableState({
                ...membersTableState,
                pagination: paginationModel,
              })
            }
            users={membersData?.results || []}
            onRemoveMemberFromTeamGroup={setMemberToDelete}
            disabled={!isAdmin}
          />
        </Box>
      </PageContentLayout>
      {memberToDelete && (
        <ConfirmActionModal
          open
          title="Elimina membro dal gruppo"
          description={`Sei sicuro di voler eliminare ${memberToDelete.email} dal gruppo ${teamGroup?.name}?`}
          handleClose={() => setMemberToDelete(null)}
          onConfirm={onConfirmRemoveMemberFromGroup}
        />
      )}
    </>
  );
};

export default TeamGroupDetailPage;
